import React, { useEffect, useMemo, useState } from 'react';
import Dialog from './TivigoDialog';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LiveTvRoundedIcon from '@mui/icons-material/LiveTvRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MovieRoundedIcon from '@mui/icons-material/MovieRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import TivigoToast from './TivigoToast';
import './userPanelCategoryModal.css';

export const EXCLUDED_BOUQUET_IDS = ['1', '2', '3', '16', '17', '106'];
export const MANDATORY_BOUQUET_IDS = ['18', '20', '21', '22', '23', '24', '25', '26'];
export const ADULT_BOUQUET_IDS = ['37', '58'];

const SERIES_TERMS = [
  'final yapan diziler',
  'netflix',
  'disney',
  'amazon',
  'hbo',
  'blutv',
  'puhu',
  'gain',
  'tod',
  'exxen',
  'mubi',
  'hulu',
  'tabii',
  'apple tv',
  'tiviplus',
  'pazartesi dizileri',
  'salı dizileri',
  'çarşamba dizileri',
  'perşembe dizileri',
  'cuma dizileri',
  'cumartesi dizileri',
  'pazar dizileri',
];

const PREVIEW_BOUQUETS = [
  { id: '18', bouquet_name: 'Türkiye: Ulusal Kanallar' },
  { id: '20', bouquet_name: 'Türkiye: Haber' },
  { id: '21', bouquet_name: 'Türkiye: Spor' },
  { id: '22', bouquet_name: 'Türkiye: Belgesel' },
  { id: '23', bouquet_name: 'Türkiye: Çocuk' },
  { id: '24', bouquet_name: 'Türkiye: Müzik' },
  { id: '25', bouquet_name: 'Türkiye: Yerel Kanallar' },
  { id: '26', bouquet_name: 'Türkiye: Yedek Kanallar' },
  { id: '31', bouquet_name: 'Türkiye: Eğlence' },
  { id: '32', bouquet_name: 'Avrupa Kanalları' },
  { id: '37', bouquet_name: 'Canlı: Yetişkinler' },
  { id: '41', bouquet_name: 'Filmler: Türk Sineması' },
  { id: '42', bouquet_name: 'Film: Vizyondakiler' },
  { id: '58', bouquet_name: 'Filmler: Yetişkinler' },
  { id: '50', bouquet_name: 'Netflix' },
  { id: '51', bouquet_name: 'Disney+' },
  { id: '52', bouquet_name: 'Amazon Prime Video' },
];

const PREVIEW_USER_BOUQUETS = [
  ...MANDATORY_BOUQUET_IDS,
  '31',
  '41',
  '50',
];

const CATEGORY_DEFINITIONS = [
  { id: 'live', label: 'Canlı', Icon: LiveTvRoundedIcon },
  { id: 'movies', label: 'Filmler', Icon: MovieRoundedIcon },
  { id: 'series', label: 'Diziler', Icon: TvRoundedIcon },
];

const normalizeId = (value) => String(value ?? '').trim();

const normalizeIds = (values) => Array.from(new Set(
  (Array.isArray(values) ? values : [])
    .map(normalizeId)
    .filter(Boolean),
));

const getBouquetName = (bouquet) => String(
  bouquet?.bouquet_name ?? bouquet?.bouquetName ?? bouquet?.name ?? '',
).trim();

const normalizeBouquets = (values) => (Array.isArray(values) ? values : [])
  .map((bouquet) => ({
    ...bouquet,
    id: normalizeId(bouquet?.id ?? bouquet?.bouquet_id ?? bouquet?.bouquetId),
    bouquet_name: getBouquetName(bouquet),
  }))
  .filter((bouquet) => bouquet.id && bouquet.bouquet_name);

export const normalizeBouquetResponse = (result) => {
  if (result?.success === false) {
    throw new Error(result.message || 'Kategori listesi alınamadı.');
  }

  const source = result?.data || result || {};
  return {
    allBouquets: normalizeBouquets(
      source.all_bouquets ?? source.allBouquets ?? source.bouquets ?? (Array.isArray(source) ? source : []),
    ),
    userBouquetIds: normalizeIds(
      source.user_bouquets ?? source.userBouquets ?? source.selected_bouquets ?? source.selectedBouquets,
    ),
  };
};

const normalizeName = (value) => String(value || '').trim().toLocaleLowerCase('tr-TR');
const isFilmBouquet = (name) => name.startsWith('film:') || name.startsWith('filmler:');
const isSeriesBouquet = (name) => SERIES_TERMS.some((term) => name.includes(term));

export const getCategoryId = (bouquet) => {
  const name = normalizeName(bouquet.bouquet_name);
  if (isFilmBouquet(name)) return 'movies';
  if (isSeriesBouquet(name)) return 'series';
  return 'live';
};

const getUsername = (userInfo, username) => {
  const info = userInfo?.user_info || {};
  const user = userInfo?.user || {};
  return username || info.username || user.username || userInfo?.username || 'Tivigo kullanıcısı';
};

const setsAreEqual = (first, second) => {
  const left = normalizeIds(first).sort();
  const right = normalizeIds(second).sort();
  return left.length === right.length && left.every((id, index) => id === right[index]);
};

const ensureUpdateSucceeded = (result) => {
  if (result?.success === false) {
    throw new Error(result.message || 'Kategori değişiklikleri kaydedilemedi.');
  }
  return result;
};

function ToggleSwitch({ checked, disabled = false, label, tabIndex = 0, tone = 'green', onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`tvup-category-switch tone-${tone}${checked ? ' is-checked' : ''}`}
      disabled={disabled}
      tabIndex={tabIndex}
      onClick={(event) => {
        event.stopPropagation();
        onChange?.(!checked);
      }}
    >
      <span />
    </button>
  );
}

function UserPanelCategoryModal({
  open,
  onClose,
  userInfo,
  username,
  targetUserId = null,
  previewMode = false,
  onLoadBouquets,
  onUpdateBouquets,
  onSuccess,
}) {
  const accountName = getUsername(userInfo, username);
  const [activeCategory, setActiveCategory] = useState('live');
  const [allBouquets, setAllBouquets] = useState([]);
  const [originalIds, setOriginalIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setActiveCategory('live');
    setAllBouquets([]);
    setOriginalIds([]);
    setSelectedIds([]);
    setLoading(true);
    setSaving(false);
    setNotice(null);

    const load = async () => {
      try {
        const response = previewMode
          ? { all_bouquets: PREVIEW_BOUQUETS, user_bouquets: PREVIEW_USER_BOUQUETS }
          : typeof onLoadBouquets === 'function'
            ? await onLoadBouquets({ userInfo, targetUserId })
            : null;

        if (!response) {
          throw new Error('Kategori listeleme bağlantısı henüz sağlanmadı.');
        }

        const normalized = normalizeBouquetResponse(response);
        const selectedWithMandatory = normalizeIds([
          ...normalized.userBouquetIds,
          ...MANDATORY_BOUQUET_IDS,
        ]);

        if (!cancelled) {
          setAllBouquets(normalized.allBouquets);
          setOriginalIds(normalized.userBouquetIds);
          setSelectedIds(selectedWithMandatory);
        }
      } catch (error) {
        if (!cancelled) {
          setNotice({ type: 'error', text: error.message || 'Kategori listesi yüklenemedi.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [onLoadBouquets, open, previewMode, targetUserId, userInfo]);

  useEffect(() => {
    if (!notice) return undefined;

    const dismissTimer = window.setTimeout(() => {
      setNotice(null);
    }, 3600);

    return () => window.clearTimeout(dismissTimer);
  }, [notice]);

  const bouquetsByCategory = useMemo(() => Object.fromEntries(
    CATEGORY_DEFINITIONS.map(({ id }) => [
      id,
      allBouquets.filter((bouquet) => (
        !EXCLUDED_BOUQUET_IDS.includes(bouquet.id) && getCategoryId(bouquet) === id
      )),
    ]),
  ), [allBouquets]);

  const visibleBouquets = bouquetsByCategory[activeCategory] || [];
  const activeCategoryIndex = Math.max(
    0,
    CATEGORY_DEFINITIONS.findIndex(({ id }) => id === activeCategory),
  );
  const categoryTabIndicatorX = [
    '0px',
    'calc(100% + 5px)',
    'calc(200% + 10px)',
  ][activeCategoryIndex];

  const selectedSet = useMemo(() => new Set(normalizeIds(selectedIds)), [selectedIds]);
  const allBouquetIdSet = useMemo(() => new Set(allBouquets.map((bouquet) => bouquet.id)), [allBouquets]);
  const adultIdsInList = ADULT_BOUQUET_IDS.filter((id) => allBouquetIdSet.has(id));
  const adultChecked = adultIdsInList.length > 0 && adultIdsInList.every((id) => selectedSet.has(id));
  const bulkCandidates = visibleBouquets.filter((bouquet) => (
    !MANDATORY_BOUQUET_IDS.includes(bouquet.id) && !ADULT_BOUQUET_IDS.includes(bouquet.id)
  ));
  const allChecked = bulkCandidates.length > 0 && bulkCandidates.every((bouquet) => selectedSet.has(bouquet.id));
  const hasChanges = !setsAreEqual(originalIds, selectedIds);

  const categoryStats = useMemo(() => Object.fromEntries(CATEGORY_DEFINITIONS.map((category) => {
    const items = bouquetsByCategory[category.id] || [];
    return [category.id, {
      total: items.length,
      active: items.filter((bouquet) => selectedSet.has(bouquet.id)).length,
    }];
  })), [bouquetsByCategory, selectedSet]);

  const setBouquetState = (bouquetId, checked) => {
    const id = normalizeId(bouquetId);
    if (MANDATORY_BOUQUET_IDS.includes(id) && !checked) {
      setNotice({ type: 'warning', text: 'Bu paket zorunlu olduğu için kapatılamaz.' });
      return;
    }

    const next = new Set(selectedSet);
    if (checked) next.add(id);
    else next.delete(id);
    MANDATORY_BOUQUET_IDS.forEach((mandatoryId) => next.add(mandatoryId));
    setSelectedIds(Array.from(next));
    setNotice(null);
  };

  const toggleAdults = (checked) => {
    if (adultIdsInList.length === 0) {
      setNotice({ type: 'warning', text: 'Yetişkin içerik paketleri kategori listesinde bulunamadı.' });
      return;
    }

    const next = new Set(selectedSet);
    adultIdsInList.forEach((id) => {
      if (checked) next.add(id);
      else next.delete(id);
    });
    setSelectedIds(Array.from(next));
    setNotice(null);
  };

  const toggleAllInCategory = (checked) => {
    const next = new Set(selectedSet);
    bulkCandidates.forEach((bouquet) => {
      if (checked) next.add(bouquet.id);
      else next.delete(bouquet.id);
    });
    MANDATORY_BOUQUET_IDS.forEach((mandatoryId) => next.add(mandatoryId));
    setSelectedIds(Array.from(next));
    setNotice(null);
  };

  const resetChanges = () => {
    setSelectedIds(normalizeIds([...originalIds, ...MANDATORY_BOUQUET_IDS]));
    setNotice(null);
  };

  const saveChanges = async () => {
    if (!hasChanges || saving) return;

    const originalSet = new Set(normalizeIds(originalIds));
    const finalIds = normalizeIds([...selectedIds, ...MANDATORY_BOUQUET_IDS]);
    const finalSet = new Set(finalIds);
    const toAdd = finalIds.filter((id) => !originalSet.has(id));
    const toRemove = normalizeIds(originalIds).filter((id) => !finalSet.has(id));

    setSaving(true);
    setNotice(null);
    try {
      const result = previewMode
        ? { success: true, preview: true }
        : typeof onUpdateBouquets === 'function'
          ? ensureUpdateSucceeded(await onUpdateBouquets({
            userInfo,
            targetUserId,
            toAdd,
            toRemove,
            selectedBouquetIds: finalIds,
            originalBouquetIds: normalizeIds(originalIds),
          }))
          : (() => { throw new Error('Kategori güncelleme bağlantısı henüz sağlanmadı.'); })();

      setSelectedIds(finalIds);
      setOriginalIds(finalIds);
      setNotice({
        type: 'success',
        text: previewMode && result?.preview
          ? 'Kategori seçimi tasarım önizlemesinde kaydedildi.'
          : 'Kanal paketleri başarıyla güncellendi.',
      });
      if (!previewMode) {
        onSuccess?.({
          result,
          selectedBouquetIds: finalIds,
          addedBouquetIds: toAdd,
          removedBouquetIds: toRemove,
        });
      }
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Kategori değişiklikleri kaydedilemedi.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-category-dialog"
      aria-labelledby="tvup-category-modal-title"
      aria-describedby="tvup-category-modal-description"
      slotProps={{
        backdrop: { className: 'tvup-category-backdrop' },
        paper: { id: 'tvup-category-modal', className: 'tvup-category-modal' },
      }}
    >
      <header className="tvup-category-header">
        <span className="tvup-category-title-icon" aria-hidden="true"><LiveTvRoundedIcon /></span>
        <div className="tvup-category-title-copy">
          <h2 id="tvup-category-modal-title">Kategorileri Yönet</h2>
          <p id="tvup-category-modal-description"><span>{accountName}</span> için kanal paketlerini düzenleyin.</p>
        </div>
        <button type="button" className="tvup-category-close" aria-label="Kategori yönetimi penceresini kapat" onClick={onClose}>
          <CloseRoundedIcon />
        </button>
      </header>

      <TivigoToast notice={notice} />

      <nav
        className="tvup-category-tabs"
        role="tablist"
        aria-label="Kategori türleri"
        style={{ '--tvup-category-tab-x': categoryTabIndicatorX }}
      >
        {CATEGORY_DEFINITIONS.map(({ id, label, Icon }) => {
          const stats = categoryStats[id] || { active: 0, total: 0 };
          return (
            <button
              type="button"
              key={id}
              role="tab"
              id={`tvup-category-tab-${id}`}
              aria-controls={`tvup-category-panel-${id}`}
              aria-selected={activeCategory === id}
              className={activeCategory === id ? 'is-active' : ''}
              onClick={() => {
                setActiveCategory(id);
                setNotice(null);
              }}
            >
              <Icon />
              <span>{label}</span>
              <small>{stats.active}/{stats.total}</small>
            </button>
          );
        })}
      </nav>

      <div className="tvup-category-controls">
        <div className="tvup-category-control tone-adult">
          <span className="tvup-category-control-icon"><SecurityRoundedIcon /></span>
          <span><b>Yetişkinler</b><small>Tüm Listedekileri Kapsar</small></span>
          <ToggleSwitch
            checked={adultChecked}
            disabled={loading || saving || adultIdsInList.length === 0}
            label="Yetişkin kategorilerini aç veya kapat"
            tone="red"
            onChange={toggleAdults}
          />
        </div>

        <div className="tvup-category-control tone-all">
          <span className="tvup-category-control-icon"><CheckCircleRoundedIcon /></span>
          <span><b>Hepsi</b><small>Bu sekmedeki paketler</small></span>
          <ToggleSwitch
            checked={allChecked}
            disabled={loading || saving || bulkCandidates.length === 0}
            label={`${CATEGORY_DEFINITIONS.find((item) => item.id === activeCategory)?.label} paketlerinin tümünü aç veya kapat`}
            onChange={toggleAllInCategory}
          />
        </div>
      </div>

      <div className="tvup-category-info tvup-category-info-static">
        <InfoOutlinedIcon />
        <p>Kategori değişikliğinden sonra güncel kanal listesi için uygulamanızı yeniden kurun.</p>
      </div>

      <div className="tvup-category-body">
        {loading ? (
          <div className="tvup-category-loading" role="status">
            <CircularProgress size={30} thickness={4.5} />
            <b>Kategoriler yükleniyor</b>
            <span>Hesabınıza tanımlı paketler kontrol ediliyor.</span>
          </div>
        ) : (
          <>
            <div className="tvup-category-panel-window">
              <div
                className="tvup-category-panel-track"
                style={{ transform: `translate3d(-${activeCategoryIndex * 100}%, 0, 0)` }}
              >
                {CATEGORY_DEFINITIONS.map(({ id, Icon: CategoryIcon }) => {
                  const panelBouquets = bouquetsByCategory[id] || [];
                  const panelIsActive = activeCategory === id;

                  return (
                    <section
                      key={id}
                      id={`tvup-category-panel-${id}`}
                      className="tvup-category-panel-slide"
                      role="tabpanel"
                      aria-labelledby={`tvup-category-tab-${id}`}
                      aria-hidden={!panelIsActive}
                    >
                      <div className="tvup-category-list">
                        {panelBouquets.length ? panelBouquets.map((bouquet) => {
                          const checked = selectedSet.has(bouquet.id);
                          const mandatory = MANDATORY_BOUQUET_IDS.includes(bouquet.id);
                          return (
                            <article
                              key={bouquet.id}
                              className={`tvup-category-card${checked ? ' is-active' : ''}${mandatory ? ' is-mandatory' : ''}`}
                              onClick={() => panelIsActive && !mandatory && !saving && setBouquetState(bouquet.id, !checked)}
                            >
                              <span className="tvup-category-card-icon"><CategoryIcon /></span>
                              <span className="tvup-category-card-copy">
                                <b title={bouquet.bouquet_name}>{bouquet.bouquet_name}</b>
                                {mandatory && <span className="tvup-category-required"><LockRoundedIcon /> Zorunlu</span>}
                              </span>
                              <ToggleSwitch
                                checked={checked}
                                disabled={saving || mandatory || !panelIsActive}
                                tabIndex={panelIsActive ? 0 : -1}
                                label={`${bouquet.bouquet_name} kategorisini ${checked ? 'kapat' : 'aç'}`}
                                onChange={(nextChecked) => setBouquetState(bouquet.id, nextChecked)}
                              />
                            </article>
                          );
                        }) : (
                          <div className="tvup-category-empty">
                            <TvRoundedIcon />
                            <b>Bu kategoride paket bulunamadı</b>
                            <span>Seçili kategori için tanımlanmış bir paket yok.</span>
                          </div>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <footer className="tvup-category-footer">
        <div className="tvup-category-footer-status">
          <span className={hasChanges ? 'has-change' : ''} />
          <p>{hasChanges ? 'Kaydedilmemiş değişiklikleriniz var.' : 'Kategori seçimleriniz güncel.'}</p>
        </div>
        <button type="button" className="tvup-category-reset" disabled={!hasChanges || saving} onClick={resetChanges}>
          <RestartAltRoundedIcon /> Geri Al
        </button>
        <button type="button" className="tvup-category-save" disabled={!hasChanges || saving || loading} onClick={saveChanges}>
          {saving ? <CircularProgress size={17} thickness={5} /> : <SaveRoundedIcon />}
          {saving ? 'Kaydediliyor' : 'Kaydet'}
        </button>
      </footer>
    </Dialog>
  );
}

export default UserPanelCategoryModal;
